import { Line, Point } from "../types/canvasTypes";
import { calculateLineLength } from "./util";

function breakIntoChunksOfGap10(line: Line, scale: number) {
  if (!line.parallelLine) return;

  const lineLength = calculateLineLength(line, scale);
  let numberOfGlass = (line.pannels?.length || 0) + line.numberOfDoors;

  let chunkLength = (lineLength / numberOfGlass) * scale;

  return breakLineIntoChunks(
    line?.parallelLine,
    chunkLength,
    10,
    numberOfGlass
  );
}

function breakLineIntoChunks(
  line: Line,
  chunkLength: number,
  gapLength: number,
  numberOfGlass: number
): Line[] {
  const { start, end, angle } = line;
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  chunkLength = chunkLength - gapLength;

  const length = Math.sqrt(dx ** 2 + dy ** 2);

  const chunkLines: Line[] = [];
  let currentPosition = 0;

  while (numberOfGlass > 0) {
    const currentLength = chunkLength;

    const chunkStart: Point = {
      x: start.x + (dx / length) * currentPosition,
      y: start.y + (dy / length) * currentPosition,
    };
    const chunkEnd: Point = {
      x: start.x + (dx / length) * (currentPosition + currentLength),
      y: start.y + (dy / length) * (currentPosition + currentLength),
    };

    chunkLines.push({
      start: chunkStart,
      end: chunkEnd,
      angle,
      numberOfDoors: 0,
    });

    currentPosition += currentLength + gapLength;
    numberOfGlass = numberOfGlass - 1;
  }

  return chunkLines;
}

export function redrawCanvas(
  lines: Line[],
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  scale: number
) {
  //1. clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  canvas.style.backgroundColor = "#FFFFFF";

  // 2. draw lines
  lines.forEach((line) => {
    ctx.strokeStyle = "black";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(line.start.x, line.start.y);
    ctx.lineTo(line.end.x, line.end.y);
    ctx.stroke();

    if (line.parallelLine) {
      // 3. draw glass lines
      const lineLength = calculateLineLength(line, scale);
      const chunks = breakIntoChunksOfGap10(line, scale);

      if (!chunks) return;

      // 4. draw glass lines chuncks
      let count = 0;
      let doorsNeeded = line.numberOfDoors;
      let rPannels = line.pannels?.filter((pannel) => pannel.isRPannel);
      let regularPannel = line.pannels?.filter((pannel) => !pannel.isRPannel);
      chunks.forEach((chunk, index) => {
        if (
          doorsNeeded === 2 &&
          (index === chunks.length - 3 || index === chunks.length - 2)
        ) {
          ctx.strokeStyle = "blue";
          ctx.lineWidth = 4;
        } else if (doorsNeeded === 1 && index === chunks.length - 1) {
          ctx.strokeStyle = "blue";
          ctx.lineWidth = 4;
          ctx.fillStyle = "red";
        } else {
          ctx.strokeStyle = "#add8e6";
          ctx.lineWidth = 3;
        }
        ctx.beginPath();

        ctx.moveTo(chunk.start.x, chunk.start.y);
        ctx.lineTo(chunk.end.x, chunk.end.y);

        ctx.stroke();

        if (line.pannels) {
          count = line.pannels?.length || 0;
          const dx = chunk.end.x - chunk.start.x;
          const dy = chunk.end.y - chunk.start.y;
          let angle = Math.atan2(dy, dx);
          ctx.save();
          // Calculate angle between chunk and horizontal axis
          const midpointX = (chunk.start.x + chunk.end.x) / 2;
          const midpointY = (chunk.start.y + chunk.end.y) / 2;

          ctx.translate(midpointX, midpointY);

          // Rotate the canvas based on the angle
          ctx.rotate(angle);
          if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
            angle += Math.PI;
          }
          // if ((angle < 3.15 && angle > 3.1) || angle < -3.12) angle = 0;
          // Save current state of the canvas
          if (
            doorsNeeded === 2 &&
            rPannels &&
            rPannels?.length > 0 &&
            (index === chunks.length - 1 || index === chunks.length - 4)
          ) {
            ctx.fillStyle = "red";
            ctx.font = "14px sans-serif";
            ctx.fillText(`${rPannels.pop()?.length}R''`, 0, 0);
          } else if (
            rPannels &&
            rPannels?.length > 0 &&
            index === chunks.length - 2 &&
            doorsNeeded === 1
          ) {
            ctx.fillStyle = "red";
            ctx.font = "14px sans-serif";
            ctx.fillText(`${rPannels.pop()?.length}R''`, 0, 0);
          } else if (index === chunks.length - 1 && doorsNeeded === 1) {
            ctx.fillStyle = "red";
            ctx.font = "14px sans-serif";
            ctx.fillText(`${36}D''`, 0, 0);
          } else if (
            doorsNeeded === 2 &&
            (index === chunks.length - 2 || index === chunks.length - 3)
          ) {
            ctx.fillStyle = "red";
            ctx.font = "14px sans-serif";
            ctx.fillText(`${36}D''`, 0, 0);
          } else {
            ctx.fillStyle = "red";
            ctx.font = "14px sans-serif";
            ctx.fillText(`${regularPannel!.pop()?.length}''`, 0, 0);
          }

          ctx.restore();
        }
      });

      // Calculate angle between chunk and horizontal axis
      const dx = line.end.x - line.start.x;
      const dy = line.end.y - line.start.y;
      let angle = Math.atan2(dy, dx);
      if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
        angle += Math.PI;
      }

      // Save current state of the canvas
      ctx.save();

      const midpointX = (line.start.x + line.end.x) / 2;
      const midpointY = (line.start.y + line.end.y) / 2;
      ctx.translate(midpointX, midpointY);
      let pannelsLengthSum: number =
        line.pannels?.reduce(
          (totalLength, panel) =>
            totalLength + panel.length + (panel.isRPannel ? 36 : 0),
          0
        ) || 0;

      if (pannelsLengthSum) {
        pannelsLengthSum = lineLength - pannelsLengthSum;
      }
      // Rotate the canvas based on the angle
      ctx.rotate(angle);
      ctx.fillStyle = "black";
      ctx.font = "20px sans-serif";
      ctx.fillText(`${lineLength.toFixed(2)} ''`, -20, -30);
      ctx.font = "15px sans-serif";
      // ctx.fillStyle = "gray";
      // ctx.fillText(`gap: ${pannelsLengthSum?.toFixed(2)}''`, -20, -50);
      console.info(
        `${lineLength.toFixed(2)} '' - gap: ${pannelsLengthSum?.toFixed(2)}''`
      );
      ctx.restore();
    }

    ctx.strokeStyle = "black";
    ctx.lineWidth = 5;
  });
}
